/**
 * UserCodeEngine - ESBuild in a browser-friendly wrapper for running user provided code.
 * Compiles TypeScript strings and dynamically imports the result at runtime.
 */

import * as esbuild from 'esbuild-wasm';

import { createInjectedDependencies } from '../runtime/InjectedDependencies.js';


import { isNode } from './Utils.js';


/**
 * UserCodeEngine provides functionality to compile and execute TypeScript code in the browser.
 * It uses ESBuild WebAssembly to compile TypeScript to JavaScript and supports dynamic imports
 * from CDNs like esm.sh. Results are cached to improve performance for repeated compilations.
 *
 * Example usage:
 * ```typescript
 * const engine = new UserCodeEngine();
 * await engine.initialize();
 *
 * const code = `
 *   export default function() {
 *     console.log('Hello World!');
 *   }
 * `;
 *
 * const result = await engine.compile(code);
 * const module = await engine.import(result);
 * module.default(); // prints "Hello World!"
 * ```
 */
export class UserCodeEngine {
  private static esbuildInitialized: boolean = false;
  private static initializePromise: Promise<void> | null = null;
  private cdn: string;
  private compilationCache: Map<string, esbuild.BuildResult> = new Map();

  constructor(cdn: string = 'https://esm.sh') {
    this.cdn = cdn;
  }

  /**
   * Initializes the ESBuild WebAssembly module.
   * This must be called before any compilation can occur.
   *
   * @returns A promise that resolves when initialization is complete
   */
  public async initialize() {
    if (UserCodeEngine.esbuildInitialized) {
      return;
    }
    if (UserCodeEngine.initializePromise) {
      await UserCodeEngine.initializePromise;
      return;
    }

    UserCodeEngine.initializePromise = (async () => {
      const beginTime = performance.now();
      const version = esbuild.version;
      const url = this.cdnUrl(`esbuild-wasm@${version}/esbuild.wasm`);
      console.log(`Initializing ESBuild from ${url}`);
      try {
        await esbuild.initialize({
          // wasmURL is only supported in the browser
          wasmURL: isNode() ? undefined : url,
        });
      } catch (error) {
        // esbuild-wasm is process-global and throws if initialize is called after it was already initialized.
        // Treat that specific error as success and mark initialized.
        const message = error instanceof Error ? error.message : String(error);
        if (!message.includes('Cannot call "initialize" more than once')) {
          throw error;
        }
      }

      const duration = (performance.now() - beginTime) / 1000;
      UserCodeEngine.esbuildInitialized = true;
      console.log(`ESBuild initialized in ${duration.toFixed(2)} sec`);
    })();

    try {
      await UserCodeEngine.initializePromise;
    } finally {
      UserCodeEngine.initializePromise = null;
    }
  }

  /**
   * Returns whether the ESBuild WebAssembly module has been initialized.
   *
   * @returns True if the engine is initialized, false otherwise
   */
  public getIsInitialized(): boolean {
    return UserCodeEngine.esbuildInitialized;
  }

  /**
   * Compiles TypeScript code to JavaScript using ESBuild.
   * Results are cached based on the code hash to improve performance.
   *
   * @param code - The TypeScript code to compile
   * @returns A promise that resolves to the build result
   */
  public async compile(code: string): Promise<esbuild.BuildResult> {
    const codeHash = await this.hashString(code);
    console.log(`Compiling code, length: ${code.length}, hash: ${codeHash}`);

    if (this.compilationCache.has(codeHash)) {
      console.log('Using cached compilation result');
      return this.compilationCache.get(codeHash)!;
    }

    const beginTime = performance.now();

    const nodeEnv = process.env.NODE_ENV ?? 'production';

    const result = await esbuild.build({
      external: ['@gnsx/genesys.js', 'three', 'genesys.js'],
      bundle: true,
      write: false,
      format: 'cjs',
      // disable the fetch plugin to avoid bringing in arbitrary dependencies
      // plugins: [ this.fetchPlugin() ],
      define: {
        'process.env.NODE_ENV': `"${nodeEnv}"`,
      },
      stdin: {
        contents: code,
        resolveDir: '/',
        sourcefile: 'index.ts',
        loader: 'ts',
      },
      tsconfigRaw: {
        compilerOptions: {
          experimentalDecorators: true
        }
      }
    });

    const duration = (performance.now() - beginTime) / 1000;
    if (result.errors.length) {
      console.error('Compilation failed:', result.errors);
      throw new Error(`Build returned ${result.errors.length} errors`);
    }

    this.compilationCache.set(codeHash, result);
    console.log(`Code compiled in ${duration.toFixed(2)} sec`);
    return result;
  }

  // Add a method to generate a hash from a string
  private async hashString(str: string): Promise<string> {
    try {
      // Use window.crypto for browser environments
      const cryptoObj = typeof window !== 'undefined' ? window.crypto : crypto;

      if (!cryptoObj || !cryptoObj.subtle) {
        // Fallback to a simple hash if crypto is not available
        return this.simpleHash(str);
      }

      const encoder = new TextEncoder();
      const data = encoder.encode(str);
      const hashBuffer = await cryptoObj.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      // Fallback to simple hash if crypto operations fail
      return this.simpleHash(str);
    }
  }

  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16);
  }

  /**
   * Imports the compiled JavaScript module from the build result.
   *
   * @param result - The ESBuild compilation result
   * @returns A promise that resolves to the imported module or null if import fails
   */
  public async import(result: esbuild.BuildResult): Promise<any | null> {
    if (typeof window === 'undefined') return null; // Ensure client-side only
    if (!result.outputFiles?.[0]?.text) return null;

    const code = result.outputFiles[0].text;

    try {
      const exports = {};
      const module = { exports };
      const injectedDependencies = createInjectedDependencies();
      const fn = new Function('require', 'module', 'exports', code);
      fn(injectedDependencies, module, exports);
      return module.exports;
    } catch (error) {
      console.error('Failed to execute module code:', error);
      throw error;
    }
  }

  private fetchPlugin() {
    const cdn = this.cdn;
    return {
      name: 'fetch-plugin',
      setup(build: any) {
        build.onResolve({ filter: /.*/ }, (args: any) => {
          const path = args.path;

          // Already a fully qualified URL
          if (path.startsWith('https://') || path.startsWith('http://')) {
            return { path, namespace: 'esm' };
          }

          // Root-relative esm.sh paths like "/three@..."
          if (path.startsWith('/')) {
            return {
              path: `${cdn}${path}`,
              namespace: 'esm',
            };
          }

          // Relative imports (like ./module.js)
          if (path.startsWith('./') || path.startsWith('../')) {
            return {
              path: new URL(path, args.importer).toString(),
              namespace: 'esm',
            };
          }

          // Bare imports like 'three'
          return {
            path: `${cdn}/${path}`,
            namespace: 'esm',
          };
        });

        build.onLoad({ filter: /.*/, namespace: 'esm' }, async (args: any) => {
          const res = await fetch(args.path);
          if (!res.ok) throw new Error(`Failed to fetch ${args.path}`);
          const text = await res.text();
          return {
            contents: text,
            loader: 'js',
          };
        });
      },
    };
  }

  private cdnUrl(packagePath: string): string {
    return `${this.cdn}/${packagePath}`;
  }
}
